import { useState, useEffect, useRef, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';

export const PageSignIn = memo(({ onLogin }) => {
  const [mode,    setMode]  = useState("signin");
  const [email,   setEmail] = useState("");
  const [password,setPass]  = useState("");
  const [showPw,  setShowPw]= useState(false);
  const [remember,setRem]   = useState(true);
  const [loading, setLoad]  = useState(false);
  const [error,   setErr]   = useState("");
  const [success, setOk]    = useState("");
  const [vis,     setVis]   = useState(false);
  const [tapCount,setTaps]  = useState(4572);
  const [activeAv,setActive]= useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [otp,     setOtp]   = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [deactivated, setDeactivated] = useState(null); // {daysLeft, email}
  const [reactivating, setReactivating] = useState(false);
  const navigate = useNavigate();

  useEffect(()=>{ document.title = 'GOtaps - Sign In'; setTimeout(()=>setVis(true), 80); },[]);
  useEffect(()=>{
    const id = setInterval(()=>setTaps(n=>n+Math.floor(Math.random()*3+1)),2600);
    return ()=>clearInterval(id);
  },[]);
  useEffect(()=>{
    const id = setInterval(()=>setActive(n=>(n+1)%6),3000);
    return ()=>clearInterval(id);
  },[]);

  const reset = ()=>{ setErr(""); setOk(""); };

  const handleSubmit = async ()=>{
    reset();
    if(!email.trim()) { setErr("Please enter your email address."); return; }

    // ── Sign In ──
    if(mode==="signin") {
      if(!password.trim()) { setErr("Please enter your password."); return; }
      setLoad(true);
      try {
        const res = await api.login(email, password);
        const userData = res.data || { name: email.split("@")[0], email };
        onLogin(userData, res.token, remember);
      } catch(e) {
        if (e.status === 403 && e.data?.account_deactivated) {
          setDeactivated({ daysLeft: e.data.days_left || 14, email: email, password: password });
        } else {
          setErr(e.message || "Login failed. Please check your credentials.");
        }
      } finally { setLoad(false); }
      return;
    }

    // ── Forgot Password ──
    if(mode==="forgot") {
      setLoad(true);
      try {
        if(!otpSent) {
          await api.forgotPassword(email);
          setOtpSent(true);
          setOk("OTP sent to your email! Check your inbox.");
        } else if(!otp) {
          setErr("Please enter the OTP code.");
        } else if(!newPass || !confirmPass) {
          setErr("Please enter and confirm your new password.");
        } else if(newPass !== confirmPass) {
          setErr("Passwords do not match.");
        } else if(newPass.length < 6) {
          setErr("Password must be at least 6 characters.");
        } else {
          const res = await api.resetPassword(email, otp, newPass, confirmPass);
          setOk("Password reset successfully!");
          setTimeout(()=>{
            setMode("signin"); setOtpSent(false); setOtp(""); setNewPass(""); setConfirmPass("");
          }, 1500);
        }
      } catch(e) {
        setErr(e.message || "Something went wrong.");
      } finally { setLoad(false); }
      return;
    }
  };

  const handleKey = e=>{ if(e.key==="Enter") handleSubmit(); };

  const handleReactivate = async () => {
    if (!deactivated) return;
    setReactivating(true);
    try {
      const res = await api.reactivateAccount(deactivated.email, deactivated.password);
      const userData = res.data || { name: deactivated.email.split("@")[0], email: deactivated.email };
      setDeactivated(null);
      onLogin(userData, res.token, remember);
    } catch (e) {
      setDeactivated(null);
      setErr(e.message || "Failed to reactivate account.");
    } finally { setReactivating(false); }
  };

  const AVATARS = [
    {i:"JK",g:"linear-gradient(135deg,#0891b2,#06b6d4)",name:"Jake Kim",   role:"Sales Director",   co:"Accenture", leads:84,score:92},
    {i:"AR",g:"linear-gradient(135deg,#7c3aed,#a78bfa)",name:"Ana Rivera", role:"Head of Marketing",co:"Deloitte",  leads:71,score:88},
    {i:"ML",g:"linear-gradient(135deg,#059669,#34d399)",name:"Maria Lopez",role:"VP Growth",        co:"Salesforce",leads:96,score:96},
    {i:"TC",g:"linear-gradient(135deg,#d97706,#fbbf24)",name:"Tom Chen",   role:"CEO",              co:"Stripe",    leads:58,score:81},
    {i:"SL",g:"linear-gradient(135deg,#dc2626,#f87171)",name:"Sara Lin",   role:"Account Exec",     co:"HubSpot",   leads:63,score:85},
    {i:"BN",g:"linear-gradient(135deg,#475569,#94a3b8)",name:"Ben Nash",   role:"CRO",              co:"Microsoft", leads:77,score:90},
  ];

  const LOGOS = [
    {name:"Logo 1", src:process.env.PUBLIC_URL + '/logos/13489.png'},
    {name:"Logo 2", src:process.env.PUBLIC_URL + '/logos/13491.png'},
    {name:"Logo 3", src:process.env.PUBLIC_URL + '/logos/13493.png'},
    {name:"Logo 4", src:process.env.PUBLIC_URL + '/logos/13495.png'},
    {name:"Logo 5", src:process.env.PUBLIC_URL + '/logos/13497.png'},
    {name:"Logo 6", src:process.env.PUBLIC_URL + '/logos/13499.png'},
    {name:"Logo 7", src:process.env.PUBLIC_URL + '/logos/13501.png'},
    {name:"Logo 8", src:process.env.PUBLIC_URL + '/logos/13503.png'},
  ];

  return (
    <>
      <style>{`
        @keyframes orbDrift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(38px,-28px) scale(1.07)}}
        @keyframes orbDrift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-32px,22px) scale(1.05)}}
        @keyframes orbDrift3{0%,100%{transform:translate(0,0)}50%{transform:translate(22px,18px)}}
        @keyframes fa{0%,100%{transform:translateY(0px) rotate(-5deg)}50%{transform:translateY(-10px) rotate(-5deg)}}
        @keyframes fb{0%,100%{transform:translateY(0px) rotate(4deg)}50%{transform:translateY(-13px) rotate(4deg)}}
        @keyframes fc{0%,100%{transform:translateY(0px) rotate(-3deg)}50%{transform:translateY(-8px) rotate(-3deg)}}
        @keyframes fd{0%,100%{transform:translateY(0px) rotate(6deg)}50%{transform:translateY(-11px) rotate(6deg)}}
        @keyframes fe{0%,100%{transform:translateY(0px) rotate(-4deg)}50%{transform:translateY(-9px) rotate(-4deg)}}
        @keyframes ff{0%,100%{transform:translateY(0px) rotate(5deg)}50%{transform:translateY(-12px) rotate(5deg)}}
        @keyframes fg{0%,100%{transform:translateY(0px) rotate(-2deg)}50%{transform:translateY(-7px) rotate(-2deg)}}
        @keyframes fh{0%,100%{transform:translateY(0px) rotate(3deg)}50%{transform:translateY(-10px) rotate(3deg)}}
        @keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.85)}}
        @keyframes tickUp{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:none}}
        @keyframes logoScroll{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes borderTrail{0%{background-position:0%}100%{background-position:200%}}
        .si-card::before{content:'';position:absolute;top:0;left:0;right:0;height:1.5px;background:linear-gradient(90deg,transparent,rgba(100,116,139,.5),rgba(212,175,55,.45),rgba(100,116,139,.5),transparent);background-size:200%;animation:borderTrail 4s linear infinite;border-radius:30px 30px 0 0}
        .si-av-card{transition:transform .22s ease,box-shadow .22s ease}
        .si-av-card:hover{transform:translateY(-3px) scale(1.01)!important;box-shadow:0 16px 40px rgba(0,0,0,.12),inset 0 1px 0 rgba(255,255,255,.95)!important}
      `}</style>

      {/* Ambient orbs */}
      <div className="si-scene">
        <div className="si-orb si-orb-1"/>
        <div className="si-orb si-orb-2"/>
        <div className="si-orb si-orb-3"/>
        <div className="si-orb si-orb-4"/>
        <div className="si-noise"/>
      </div>

      {/* ── Card 1 · Live Tap Counter ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"5%", left:"2%",
        width:230, zIndex:6,
        background:"rgba(255,255,255,.70)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.90)",borderRadius:18,
        boxShadow:"0 8px 32px rgba(0,0,0,.08),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"17px 19px",
        opacity:vis?1:0, transform:vis?"rotate(-4deg)":"translateY(18px) rotate(-4deg)",
        transition:"opacity .6s ease .08s, transform .6s cubic-bezier(.16,1,.3,1) .08s",
        animation:"fa 8.5s ease-in-out infinite 0s",
      }}>
        <div style={{fontSize:9.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:9}}>Live Taps · Right Now</div>
        <div style={{display:"flex",alignItems:"flex-end",gap:9,marginBottom:9}}>
          <div key={tapCount} style={{fontSize:34,fontWeight:860,color:"#0f172a",letterSpacing:"-2.5px",lineHeight:1,animation:"tickUp .3s cubic-bezier(.16,1,.3,1)"}}>{tapCount.toLocaleString()}</div>
          <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:4}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 8px #22c55e",animation:"pulse 1.8s ease-in-out infinite"}}/>
            <span style={{fontSize:11,fontWeight:660,color:"#22c55e"}}>live</span>
          </div>
        </div>
        <div style={{height:5,borderRadius:3,background:"rgba(0,0,0,.06)",overflow:"hidden",marginBottom:6}}>
          <div style={{height:"100%",width:"76%",background:"linear-gradient(90deg,#0891b2,#06b6d4)",borderRadius:3}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between"}}>
          <span style={{fontSize:9.5,color:"#94a3b8"}}>Target: 6,000</span>
          <span style={{fontSize:9.5,fontWeight:660,color:"#059669"}}>↑ 23%</span>
        </div>
      </div>

      {/* ── Card 2 · Social proof ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"5%", left:"65%",
        width:222, zIndex:6,
        background:"rgba(255,255,255,.68)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.90)",borderRadius:18,
        boxShadow:"0 8px 32px rgba(0,0,0,.07),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"13px 16px",
        opacity:vis?1:0, transform:vis?"rotate(5deg)":"translateY(18px) rotate(5deg)",
        transition:"opacity .6s ease .18s, transform .6s cubic-bezier(.16,1,.3,1) .18s",
        animation:"fb 10s ease-in-out infinite .5s",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{display:"flex",flexShrink:0}}>
            {AVATARS.slice(0,5).map((av,j)=>(
              <div key={j} style={{width:26,height:26,borderRadius:"50%",background:av.g,display:"flex",alignItems:"center",justifyContent:"center",fontSize:8.5,fontWeight:800,color:"#fff",border:"2px solid rgba(255,255,255,.95)",marginLeft:j?-9:0,zIndex:5-j,flexShrink:0,boxShadow:"0 2px 6px rgba(0,0,0,.12)"}}>{av.i}</div>
            ))}
            <div style={{width:26,height:26,borderRadius:"50%",background:"rgba(100,116,139,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:7.5,fontWeight:700,color:"#64748b",border:"2px solid rgba(255,255,255,.95)",marginLeft:-9,flexShrink:0}}>+2k</div>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11,fontWeight:700,color:"#0f172a"}}>4,200+ teams joined</div>
            <div style={{display:"flex",alignItems:"center",gap:2,marginTop:3}}>
              {[0,1,2,3,4].map(s=><span key={s} style={{fontSize:9.5,color:"#f59e0b"}}>★</span>)}
              <span style={{fontSize:9,color:"#94a3b8",marginLeft:3}}>5.0 · 2.4k</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Card 3 · NFC notification ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"40%", left:"72%",
        width:238, zIndex:6,
        background:"rgba(255,255,255,.68)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.90)",borderRadius:18,
        boxShadow:"0 8px 32px rgba(0,0,0,.08),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"13px 16px",
        opacity:vis?1:0, transform:vis?"rotate(-6deg)":"translateY(18px) rotate(-6deg)",
        transition:"opacity .6s ease .28s, transform .6s cubic-bezier(.16,1,.3,1) .28s",
        animation:"fc 9s ease-in-out infinite .8s",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:11}}>
          <div style={{width:38,height:38,borderRadius:11,background:"linear-gradient(135deg,#0891b2,#06b6d4)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0,boxShadow:"0 4px 12px rgba(8,145,178,.28)"}}>💳</div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:11.5,fontWeight:720,color:"#0f172a"}}>NFC Tap — Lead</div>
            <div style={{fontSize:10,color:"#64748b",marginTop:2}}>Stark Industries · now</div>
          </div>
          <div style={{fontSize:9.5,fontWeight:660,color:"#059669",background:"rgba(5,150,105,.09)",padding:"2px 7px",borderRadius:20,border:"1px solid rgba(5,150,105,.14)",flexShrink:0}}>AI 99</div>
        </div>
      </div>

      {/* ── Card 4 · Top performer ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"18%", right:"2%",
        width:240, zIndex:6,
        background:"rgba(255,255,255,.70)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.90)",borderRadius:18,
        boxShadow:"0 8px 32px rgba(0,0,0,.08),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"16px 18px",
        opacity:vis?1:0, transform:vis?"rotate(4deg)":"translateY(18px) rotate(4deg)",
        transition:"opacity .6s ease .22s, transform .6s cubic-bezier(.16,1,.3,1) .22s",
        animation:"fd 9.5s ease-in-out infinite .3s",
      }}>
        <div style={{fontSize:9.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:11}}>🏆 Top Performer</div>
        <div style={{display:"flex",alignItems:"center",gap:11,marginBottom:11}}>
          <div style={{width:40,height:40,borderRadius:12,background:AVATARS[activeAv].g,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff",flexShrink:0,boxShadow:"0 4px 12px rgba(0,0,0,.18)",transition:"background .6s ease"}}>{AVATARS[activeAv].i}</div>
          <div style={{transition:"all .4s ease",minWidth:0}}>
            <div style={{fontSize:12.5,fontWeight:730,color:"#0f172a",letterSpacing:"-.2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{AVATARS[activeAv].name}</div>
            <div style={{fontSize:9.5,color:"#64748b",marginTop:2}}>{AVATARS[activeAv].co}</div>
          </div>
        </div>
        <div style={{display:"flex",gap:7}}>
          {[["Leads",AVATARS[activeAv].leads,"#dc2626"],["Score",AVATARS[activeAv].score,"#7c3aed"],["Taps","1.2k","#0891b2"]].map(([l,v,c])=>(
            <div key={l} style={{flex:1,textAlign:"center",padding:"6px 3px",borderRadius:8,background:`${c}09`,border:`1px solid ${c}18`}}>
              <div style={{fontSize:13,fontWeight:780,color:c,letterSpacing:"-.5px",transition:"all .4s"}}>{v}</div>
              <div style={{fontSize:8.5,color:"#94a3b8",marginTop:2,fontWeight:600}}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Card 5 · AI Insight ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"70%", left:"2%",
        width:224, zIndex:6,
        background:"rgba(255,255,255,.68)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(124,58,237,.18)",borderRadius:18,
        boxShadow:"0 8px 32px rgba(0,0,0,.07),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"13px 16px",
        opacity:vis?1:0, transform:vis?"rotate(-3deg)":"translateY(18px) rotate(-3deg)",
        transition:"opacity .6s ease .38s, transform .6s cubic-bezier(.16,1,.3,1) .38s",
        animation:"fe 11s ease-in-out infinite 1.2s",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <div style={{width:24,height:24,borderRadius:7,background:"rgba(124,58,237,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11}}>✦</div>
          <span style={{fontSize:11,fontWeight:700,color:"#7c3aed"}}>AI Insight</span>
        </div>
        <div style={{fontSize:10.5,color:"#475569",lineHeight:1.65}}>NFC leads convert <b style={{color:"#0f172a"}}>3.2×</b> faster. Best window: <b style={{color:"#0f172a"}}>Tue–Thu 10am</b></div>
      </div>

      {/* ── Card 6 · Campaign ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"52%", right:"2%",
        width:228, zIndex:6,
        background:"rgba(255,255,255,.68)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.90)",borderRadius:18,
        boxShadow:"0 8px 32px rgba(0,0,0,.07),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"13px 16px",
        opacity:vis?1:0, transform:vis?"rotate(6deg)":"translateY(18px) rotate(6deg)",
        transition:"opacity .6s ease .32s, transform .6s cubic-bezier(.16,1,.3,1) .32s",
        animation:"ff 10.5s ease-in-out infinite .7s",
      }}>
        <div style={{fontSize:9.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:9}}>Campaign Running</div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9}}>
          <div style={{width:7,height:7,borderRadius:"50%",background:"#22c55e",boxShadow:"0 0 7px #22c55e",animation:"pulse 2s infinite",flexShrink:0}}/>
          <span style={{fontSize:11,fontWeight:640,color:"#0f172a"}}>Q2 Sales Push · 48 members</span>
        </div>
        <div style={{height:5,borderRadius:3,background:"rgba(0,0,0,.06)",marginBottom:6,overflow:"hidden"}}>
          <div style={{width:"64%",height:"100%",borderRadius:3,background:"linear-gradient(90deg,#059669,#34d399)"}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:9.5,color:"#94a3b8"}}>
          <span>2,841 taps</span>
          <span style={{color:"#059669",fontWeight:660}}>64% of goal</span>
        </div>
      </div>

      {/* ── Card 7 · Online team ── */}
      <div className="si-av-card" style={{
        position:"fixed", bottom:"3%", left:"30%",
        width:218, zIndex:6,
        background:"rgba(255,255,255,.66)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.88)",borderRadius:18,
        boxShadow:"0 8px 28px rgba(0,0,0,.07),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"13px 15px",
        opacity:vis?1:0, transform:vis?"rotate(-5deg)":"translateY(18px) rotate(-5deg)",
        transition:"opacity .6s ease .44s, transform .6s cubic-bezier(.16,1,.3,1) .44s",
        animation:"fg 12s ease-in-out infinite .2s",
      }}>
        <div style={{fontSize:9.5,fontWeight:700,color:"#94a3b8",letterSpacing:".1em",textTransform:"uppercase",marginBottom:9}}>Online Now</div>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {AVATARS.slice(0,3).map((av,j)=>(
            <div key={j} style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{width:26,height:26,borderRadius:8,background:av.g,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:"#fff",flexShrink:0,boxShadow:"0 2px 6px rgba(0,0,0,.1)"}}>{av.i}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:10.5,fontWeight:640,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{av.name}</div>
                <div style={{fontSize:9,color:"#94a3b8"}}>{av.co}</div>
              </div>
              <div style={{width:7,height:7,borderRadius:"50%",background:j===activeAv%3?"#22c55e":"#cbd5e1",boxShadow:j===activeAv%3?"0 0 6px #22c55e":"none",transition:"all .6s",flexShrink:0}}/>
            </div>
          ))}
        </div>
      </div>

      {/* ── Card 8 · New connection ── */}
      <div className="si-av-card" style={{
        position:"fixed", top:"3%", left:"28%",
        width:220, zIndex:6,
        background:"rgba(255,255,255,.66)",backdropFilter:"blur(32px) saturate(200%)",WebkitBackdropFilter:"blur(32px) saturate(200%)",
        border:"1px solid rgba(255,255,255,.88)",borderRadius:18,
        boxShadow:"0 8px 28px rgba(0,0,0,.07),inset 0 1px 0 rgba(255,255,255,.96)",
        padding:"13px 16px",
        opacity:vis?1:0, transform:vis?"rotate(4deg)":"translateY(18px) rotate(4deg)",
        transition:"opacity .6s ease .50s, transform .6s cubic-bezier(.16,1,.3,1) .50s",
        animation:"fh 8.5s ease-in-out infinite 1.0s",
      }}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#059669,#34d399)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0,boxShadow:"0 4px 10px rgba(5,150,105,.26)"}}>🤝</div>
          <div style={{flex:1}}>
            <div style={{fontSize:11.5,fontWeight:700,color:"#0f172a"}}>New Connection</div>
            <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>Bruce Wayne saved your card</div>
          </div>
          <div style={{fontSize:9.5,color:"#94a3b8",flexShrink:0,marginLeft:4}}>2m ago</div>
        </div>
      </div>

      {/* ══ CENTER — Sign in card ══ */}
      <div style={{
        position:"fixed", inset:0, zIndex:10,
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:"20px", pointerEvents:"none",
      }}>
        <div style={{
          pointerEvents:"all",
          opacity:vis?1:0,
          transform:vis?"none":"translateY(20px) scale(.97)",
          transition:"opacity .55s ease 0s, transform .55s cubic-bezier(.16,1,.3,1) 0s",
        }}>
          <div className="si-card">
            <div className="si-logo">
              <img src={process.env.PUBLIC_URL + '/logo.png'} alt="GOtaps" style={{height:40,objectFit:'contain'}}/>
              <div className="si-logo-text">
                <span className="si-logo-tag">Enterprise</span>
              </div>
            </div>

            {mode==="signin"&&<><div className="si-title">Welcome back</div><div className="si-sub">Sign in to your workspace to continue</div></>}
            {mode==="forgot"&&<><div className="si-title">Reset password</div><div className="si-sub">We'll send you a secure link instantly</div></>}

            {error   && <div className="si-error">  <span>⚠️</span> {error}</div>}
            {success && <div className="si-success"><span>✅</span> {success}</div>}

            <div className="si-inp-wrap"><span className="si-inp-ico">📧</span><input className="si-inp" type="email" placeholder="Email address" value={email} onChange={e=>{setEmail(e.target.value);reset();}} onKeyDown={handleKey} disabled={mode==="forgot"&&otpSent}/></div>
            {mode==="signin"&&<div className="si-inp-wrap"><span className="si-inp-ico">🔒</span><input className="si-inp" type={showPw?"text":"password"} placeholder="Password" value={password} onChange={e=>{setPass(e.target.value);reset();}} onKeyDown={handleKey} style={{paddingRight:44}}/><button className="si-inp-toggle" tabIndex={-1} onClick={()=>setShowPw(v=>!v)}>{showPw?"🙈":"👁"}</button></div>}
            {mode==="forgot"&&otpSent&&<>
              <div className="si-inp-wrap"><span className="si-inp-ico">🔑</span><input className="si-inp" placeholder="Enter OTP code" value={otp} onChange={e=>{setOtp(e.target.value);reset();}} onKeyDown={handleKey} maxLength={6}/></div>
              <div className="si-inp-wrap"><span className="si-inp-ico">🔒</span><input className="si-inp" type="password" placeholder="New password" value={newPass} onChange={e=>{setNewPass(e.target.value);reset();}} onKeyDown={handleKey}/></div>
              <div className="si-inp-wrap"><span className="si-inp-ico">🔒</span><input className="si-inp" type="password" placeholder="Confirm new password" value={confirmPass} onChange={e=>{setConfirmPass(e.target.value);reset();}} onKeyDown={handleKey}/></div>
            </>}
            {mode==="signin"&&<div className="si-row"><label className="si-check-wrap"><input type="checkbox" className="si-check" checked={remember} onChange={e=>setRem(e.target.checked)}/><span className="si-check-lbl">Remember me</span></label><button className="si-forgot" onClick={()=>{setMode("forgot");setOtpSent(false);setOtp("");setNewPass("");setConfirmPass("");reset();}}>Forgot password?</button></div>}

            <button className="si-btn" disabled={loading} onClick={handleSubmit} style={{display:"flex",alignItems:"center",justifyContent:"center",gap:9}}>
              {loading?<><div className="si-spinner"/>{mode==="signin"?"Signing in…":otpSent?"Resetting password…":"Sending OTP…"}</>:<><span>{mode==="signin"?"Sign In":otpSent?"Reset Password":"Send OTP"}</span><span style={{opacity:.7,fontSize:16}}>→</span></>}
            </button>

            <div className="si-footer">
              {mode==="signin"&&<>Don't have an account? <button className="si-footer-link" onClick={()=>navigate('/signup')}>Register Enterprise</button></>}
              {mode==="forgot"&&<button className="si-footer-link" onClick={()=>{setMode("signin");reset();}}>← Back to sign in</button>}
            </div>

            {mode==="signin"&&<div className="si-features">
              {[{ico:"💳",lbl:"NFC Digital Cards"},{ico:"✦",lbl:"AI Lead Scoring"},{ico:"📡",lbl:"Smart Campaigns"}].map(({ico,lbl})=>(
                <div key={lbl} className="si-feat"><div className="si-feat-ico">{ico}</div><div className="si-feat-lbl">{lbl}</div></div>
              ))}
            </div>}

            {mode==="signin"&&<div style={{marginTop:14,paddingTop:12,borderTop:"1px solid rgba(0,0,0,.06)"}}>
              <div style={{fontSize:8.5,fontWeight:600,color:"#94a3b8",textAlign:"center",letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>Trusted by teams at</div>
              <div style={{overflow:"hidden",maskImage:"linear-gradient(90deg,transparent,#000 18%,#000 82%,transparent)",WebkitMaskImage:"linear-gradient(90deg,transparent,#000 18%,#000 82%,transparent)"}}>
                <div style={{display:"flex",gap:14,animation:"logoScroll 20s linear infinite",width:"max-content"}}>
                  {[...LOGOS,...LOGOS].map(({name,src},i)=>(
                    <div key={i} title={name} style={{display:"flex",alignItems:"center",justifyContent:"center",padding:"5px 12px",borderRadius:8,background:"rgba(0,0,0,.03)",border:"1px solid rgba(0,0,0,.05)",opacity:.42,flexShrink:0}}>
                      <img src={src} alt={name} style={{height:20,width:"auto",maxWidth:80,objectFit:"contain",display:"block"}}/>
                    </div>
                  ))}
                </div>
              </div>
            </div>}
          </div>
        </div>
      </div>
      {/* ══ Deactivated Account Modal ══ */}
      {deactivated && (
        <div style={{ position:"fixed", inset:0, zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
          <div onClick={()=>setDeactivated(null)} style={{ position:"absolute", inset:0, background:"rgba(10,12,20,.5)" }} />
          <div style={{
            position:"relative", width:"100%", maxWidth:440,
            background:"#fff", borderRadius:22, padding:"32px 36px",
            boxShadow:"0 32px 80px rgba(0,0,0,.2),0 8px 32px rgba(0,0,0,.1)",
            animation:"fadeIn .25s ease",
          }}>
            {/* Warning icon */}
            <div style={{ width:60, height:60, borderRadius:16, background:"rgba(220,38,38,.08)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 16px" }}>⚠️</div>

            <div style={{ fontSize:20, fontWeight:720, color:"#0f172a", textAlign:"center", marginBottom:8, fontFamily:"var(--fd)" }}>Account Deactivated</div>

            <div style={{ fontSize:13, color:"#475569", textAlign:"center", lineHeight:1.7, marginBottom:20 }}>
              Your account has been deactivated and is currently in a <b style={{color:"#dc2626"}}>{deactivated.daysLeft}-day cooldown period</b>. After this period, your account and all associated data will be permanently deleted.
            </div>

            <div style={{ padding:"14px 18px", background:"rgba(220,38,38,.05)", border:"1px solid rgba(220,38,38,.15)", borderRadius:12, marginBottom:20 }}>
              <div style={{ fontSize:11.5, fontWeight:650, color:"#dc2626", marginBottom:6 }}>What happens after cooldown:</div>
              <ul style={{ fontSize:12, color:"#64748b", lineHeight:1.7, paddingLeft:16, margin:0 }}>
                <li>All leads and analytics will be deleted</li>
                <li>Team members created by you will be removed</li>
                <li>Profiles, NFC cards, and campaigns will be erased</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button onClick={handleReactivate} disabled={reactivating} style={{
                flex:1, padding:"11px 0", borderRadius:11, border:"none", cursor:"pointer",
                background:"linear-gradient(150deg,#059669,#34d399)", color:"#fff",
                fontSize:13.5, fontWeight:680, fontFamily:"var(--f)",
                boxShadow:"0 4px 14px rgba(5,150,105,.3)",
                transition:"all .2s", opacity: reactivating ? 0.6 : 1,
              }}>
                {reactivating ? "Reactivating..." : "Reactivate Account"}
              </button>
              <button onClick={()=>setDeactivated(null)} style={{
                flex:1, padding:"11px 0", borderRadius:11,
                border:"1px solid rgba(0,0,0,.1)", background:"rgba(0,0,0,.03)",
                color:"#64748b", fontSize:13.5, fontWeight:580, cursor:"pointer",
                fontFamily:"var(--f)", transition:"all .2s",
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
